import { useEffect } from 'react';

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
  [key: string]: any;
}

interface DocumentHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  canonical?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  ogSiteName?: string;
  ogLocale?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  customMeta?: MetaTag[];
}

export const useDocumentHead = (props: DocumentHeadProps) => {
  useEffect(() => {
    const {
      title,
      description,
      keywords,
      author,
      canonical,
      robots,
      ogTitle,
      ogDescription,
      ogType,
      ogUrl,
      ogImage,
      ogSiteName,
      ogLocale,
      twitterCard,
      twitterTitle,
      twitterDescription,
      twitterImage,
      customMeta = []
    } = props;

    // 기존 메타 태그들을 저장 (클린업을 위해)
    const originalTitle = document.title;
    const addedElements: HTMLElement[] = [];

    // 제목 설정
    if (title) {
      document.title = title;
    }

    // 메타 태그 생성 및 추가하는 함수
    const addMetaTag = (attributes: Record<string, string>) => {
      const meta = document.createElement('meta');
      Object.entries(attributes).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });
      document.head.appendChild(meta);
      addedElements.push(meta);
    };

    // link 태그 생성 및 추가하는 함수
    const addLinkTag = (attributes: Record<string, string>) => {
      const link = document.createElement('link');
      Object.entries(attributes).forEach(([key, value]) => {
        link.setAttribute(key, value);
      });
      document.head.appendChild(link);
      addedElements.push(link);
    };

    // 기본 메타 태그들
    if (description) {
      addMetaTag({ name: 'description', content: description });
    }
    if (keywords) {
      addMetaTag({ name: 'keywords', content: keywords });
    }
    if (author) {
      addMetaTag({ name: 'author', content: author });
    }
    if (robots) {
      addMetaTag({ name: 'robots', content: robots });
    }

    // Open Graph 메타 태그들
    if (ogTitle) {
      addMetaTag({ property: 'og:title', content: ogTitle });
    }
    if (ogDescription) {
      addMetaTag({ property: 'og:description', content: ogDescription });
    }
    if (ogType) {
      addMetaTag({ property: 'og:type', content: ogType });
    }
    if (ogUrl) {
      addMetaTag({ property: 'og:url', content: ogUrl });
    }
    if (ogImage) {
      addMetaTag({ property: 'og:image', content: ogImage });
    }
    if (ogSiteName) {
      addMetaTag({ property: 'og:site_name', content: ogSiteName });
    }
    if (ogLocale) {
      addMetaTag({ property: 'og:locale', content: ogLocale });
    }

    // Twitter Card 메타 태그들
    if (twitterCard) {
      addMetaTag({ name: 'twitter:card', content: twitterCard });
    }
    if (twitterTitle) {
      addMetaTag({ name: 'twitter:title', content: twitterTitle });
    }
    if (twitterDescription) {
      addMetaTag({ name: 'twitter:description', content: twitterDescription });
    }
    if (twitterImage) {
      addMetaTag({ name: 'twitter:image', content: twitterImage });
    }

    // Canonical 링크
    if (canonical) {
      addLinkTag({ rel: 'canonical', href: canonical });
    }

    // 커스텀 메타 태그들
    customMeta.forEach((meta) => {
      const { content, ...attributes } = meta;
      addMetaTag({ ...attributes, content });
    });

    // 클린업 함수
    return () => {
      document.title = originalTitle;
      addedElements.forEach((element) => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
    };
  }, [props]);
};